import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { css } from "@emotion/react";
import { Card } from "main/javascripts/components/card/Card";
import { Cruise } from "main/javascripts/types/cruise";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  accentColor,
  borderColor,
} from "main/javascripts/styles/base/colorStyle";
import { ShowMoreButton } from "main/javascripts/components/button/ShowMoreButton";
import { CruiseCabinCardItem } from "./CruiseCabinCardItem";
import { fontWeight } from "main/javascripts/styles/base/typographyStyle";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import { CruiseShowParam } from "../../types/cruiseShowParam";

export interface IProps {
  cabinRates: Cruise.VesselCabinRate[];
  param: CruiseShowParam;
  isSale?: boolean;
}

export const CruiseCabinCard: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const { cabinRates, param, isSale = false } = props;

  const { t } = useTranslation([
    "component",
    "label",
    "unit",
    "button",
    "constant",
  ]);

  const defaultShowNum = 1;

  const [showMoreCabins, setShowMoreCabins] = useState(false);

  const onShowMoreButtonClick = () => {
    setShowMoreCabins(!showMoreCabins);
  };

  if (!cabinRates || cabinRates.length === 0) {
    return null;
  }

  const renderShowMoreButton = () => {
    if (cabinRates.length > defaultShowNum && !isSale) {
      const text = showMoreCabins
        ? t("button:cruise.closeMore")
        : t("button:cruise.showMoreCabins", {
            type: t(
              `constant:vesselCabinCategory.${cabinRates[0].cabinCategory}`
            ),
            count: cabinRates.length,
          });
      return (
        <div css={showMoreButtonBlockStyle}>
          <ShowMoreButton
            onClick={onShowMoreButtonClick}
            isShow={showMoreCabins}
            widthStyle="auto"
            maxWidthStyle="100%"
          >
            {text}
          </ShowMoreButton>
        </div>
      );
    }
  };

  const cardBorderColor = isSale
    ? accentColor.primaryColor
    : borderColor.secondaryLightColor;

  return (
    <Card
      styleKey="cardNoHover"
      overflowStyle="visible"
      borderStyle={`1px solid ${cardBorderColor}`}
      marginStyle={`0`}
    >
      <div css={innerStyle}>
        <div css={categoryStyle}>
          {t(`constant:vesselCabinCategory.${cabinRates[0].cabinCategory}`)}
        </div>
        <div>
          {cabinRates.map((cabinRate, index) => {
            if (!showMoreCabins && index >= defaultShowNum && !isSale) {
              return "";
            }
            return (
              <CruiseCabinCardItem
                key={cabinRate.cabinCode}
                cabinRate={cabinRate}
                param={param}
              />
            );
          })}
          {renderShowMoreButton()}
        </div>
      </div>
    </Card>
  );
};

const innerStyle = css`
  width: 100%;
`;
const categoryStyle = css`
  font-size: 1.25rem;
  line-height: 1.2;
  font-weight: ${fontWeight.medium};
  padding: ${space.atom2x} ${space.atom2x} 0;
  ${moreThanBreakpoint("tablet")} {
    padding: ${space.atom2_5x} ${space.atom2_5x} 0;
  }
`;
const showMoreButtonBlockStyle = css`
  display: flex;
  justify-content: center;
  border-top: 0.0625rem solid ${borderColor.secondaryLightColor};
  padding: ${space.atom} ${space.atom2x} ${space.atom2x};
`;
